import { useMemo, useState } from "react";
import {
  MANUAL,
  searchManual,
  type ManualBlock,
  type ManualSection,
} from "./manualContent";

/**
 * 사용 설명서.
 *
 * 목차 → 본문 구조이며, 검색하면 해당 절만 남는다.
 * 인쇄하면 전체가 그대로 나가므로 사내에 배포할 소책자로도 쓸 수 있다.
 */
export function ManualPanel() {
  const [query, setQuery] = useState("");
  const sections = useMemo(() => searchManual(query), [query]);

  const jump = (id: string) => {
    document.getElementById(`manual-${id}`)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <>
      <section className="card no-print">
        <div className="row-head">
          <h2>사용 설명서</h2>
          <div className="savebar__actions">
            <input
              className="manual__search"
              type="search"
              value={query}
              placeholder="찾을 말을 넣으십시오 (예: 배압, 채터링, 여유율)"
              onChange={(e) => setQuery(e.target.value)}
            />
            <button type="button" className="btn" onClick={() => window.print()}>
              인쇄 / PDF 저장
            </button>
          </div>
        </div>

        {query.trim() !== "" && (
          <p className="hint">
            &quot;{query}&quot; 가 들어간 절 {sections.length}개를 찾았습니다.
            {sections.length === 0 && " 다른 말로 찾아보십시오."}
          </p>
        )}

        {query.trim() === "" && (
          <>
            <p className="hint">
              처음이시면 <b>2. 처음 해보기</b> 부터 읽으십시오. 표준서 예제를 그대로
              따라 넣어보며 사용법을 익히실 수 있습니다.
            </p>
            <ol className="manual__toc">
              {MANUAL.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    className="manual__tocLink"
                    onClick={() => jump(s.id)}
                  >
                    {s.title}
                  </button>
                  <span className="muted"> — {s.summary}</span>
                </li>
              ))}
            </ol>
          </>
        )}
      </section>

      {sections.map((s) => (
        <ManualSectionView key={s.id} section={s} />
      ))}
    </>
  );
}

function ManualSectionView({ section }: { section: ManualSection }) {
  return (
    <section className="card sheet manual" id={`manual-${section.id}`}>
      <h2 className="manual__title">{section.title}</h2>
      {section.blocks.map((b, i) => (
        <Block key={i} block={b} />
      ))}
    </section>
  );
}

function Block({ block }: { block: ManualBlock }) {
  switch (block.kind) {
    case "h":
      return <h3 className="manual__h">{block.text}</h3>;

    case "p":
      return <p className="manual__p">{block.text}</p>;

    case "steps":
      return (
        <ol className="manual__steps">
          {block.items.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ol>
      );

    case "bullets":
      return (
        <ul className="manual__bullets">
          {block.items.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
      );

    case "note":
      return (
        <div className={`manual__note manual__note--${block.tone}`}>
          {block.title && (
            <strong className="manual__noteTitle">{block.title}</strong>
          )}
          <span>{block.text}</span>
        </div>
      );

    case "table":
      return (
        <div className="table-scroll">
          <table className="sheet__table sheet__table--grid manual__table">
            <thead>
              <tr>
                {block.head.map((h) => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((r, i) => (
                <tr key={i}>
                  {r.map((c, j) => (
                    <td key={j}>{c}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case "terms":
      return (
        <dl className="manual__terms">
          {block.items.map((t) => (
            <div key={t.term}>
              <dt>{t.term}</dt>
              <dd>{t.def}</dd>
            </div>
          ))}
        </dl>
      );
  }
}
